import * as Sentry from '@sentry/react';

/**
 * Centralized react-query error reporting. Wired into the app's `QueryCache` /
 * `MutationCache` so every query and mutation failure is reported to Sentry
 * without touching the existing per-call `onError` toasts (both fire).
 */

/** Pull an HTTP status code off an axios-style error, if present. */
export function getHttpStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const response = (error as { response?: { status?: unknown } }).response;
    if (response && typeof response.status === 'number') {
      return response.status;
    }
    const status = (error as { status?: unknown }).status;
    if (typeof status === 'number') {
      return status;
    }
  }
  return undefined;
}

/**
 * Only report server/network/unknown failures. Expected 4xx responses
 * (validation 400/422, auth 401/403, etc.) are routine client-side noise and
 * are surfaced to the user via toasts — reporting them would drown Sentry.
 */
export function shouldReportToSentry(error: unknown): boolean {
  const status = getHttpStatus(error);
  if (status === undefined) {
    // Network error / thrown JS error / unknown shape → report.
    return true;
  }
  // Report only 5xx (and any non-4xx status); skip the whole 4xx range.
  return status < 400 || status >= 500;
}

/** Stable, log-safe stringification of a query/mutation key. */
export function stringifyKey(key: unknown): string | undefined {
  if (key === undefined || key === null) {
    return undefined;
  }
  try {
    return JSON.stringify(key);
  } catch {
    return String(key);
  }
}

/** Report a react-query failure to Sentry (skipping expected 4xx). */
export function reportQueryError(
  error: unknown,
  key: unknown,
  kind: 'query' | 'mutation',
): void {
  if (!shouldReportToSentry(error)) {
    return;
  }

  Sentry.captureException(error, {
    tags: { source: 'react-query' },
    contexts: {
      query: {
        [kind === 'query' ? 'query_key' : 'mutation_key']: stringifyKey(key),
        http_status: getHttpStatus(error),
      },
    },
  });
}
